import React from 'react';
import { Button } from '@crazyegginc/hatch';

import { ErrorItem } from './ErrorItem';
import { SupportLinks } from '/src/support';
import { useManageSubscriptionPath } from '/src/hooks';
import { AppLink } from '/src/features/_global/components/AppLink';
import { SupportLink } from '/src/features/_global/components/SupportLink';

const DEFAULT_INSTALL_PATH = '/install';

export function RenderError({
  data: { finalUrl, error, consoleErrors = null },
  installPath = DEFAULT_INSTALL_PATH,
  CardComponent,
  ui,
}) {
  const manageSubscriptionPath = useManageSubscriptionPath();
  const Error = (props) => <ErrorItem {...props} CardComponent={CardComponent} ui={ui} />;

  switch (error.name) {
    case 'PERMISSION_REQUIRED':
      return (
        <Error
          title="Permission Required"
          description={
            <span>
              You do not have access to run the script check on this site{' '}
              <strong className="text-body-1">{finalUrl}</strong>. Check that the URL is correct, or contact the account
              owner for access to this site.
            </span>
          }
          consoleErrors={consoleErrors}
        />
      );
    case 'TRACKING_SCRIPT_CODE_MALFORMED':
      return (
        <Error
          title="Tracking Script Code Malformed"
          description="The tracking script installed on your website may have a syntax error. For example, an extra semicolon, quote, or bracket may be missing."
          suggestion={
            <span>
              Visit this{' '}
              <SupportLink className="text-link" href={SupportLinks.scriptCheck.malformedScript}>
                help article
              </SupportLink>{' '}
              to learn more.
            </span>
          }
          consoleErrors={consoleErrors}
        >
          <Button size="xl" component={SupportLink} className="mt-5" href={SupportLinks.scriptCheck.malformedScript}>
            View Help Article
          </Button>
        </Error>
      );
    case 'TRACKING_SCRIPT_CODE_COMMENTED':
      return (
        <Error
          title="Tracking Script Code Commented Out"
          description={<span>Uncomment the script section of your Tracking Script to re-enable it.</span>}
          consoleErrors={consoleErrors}
        >
          <Button
            size="xl"
            className="mt-5"
            component={AppLink}
            to={{ pathname: installPath, search: `?requestUrl=${encodeURIComponent(finalUrl)}` }}
          >
            {installPath === DEFAULT_INSTALL_PATH ? 'Open Installation Guide' : 'View Install Instructions'}
          </Button>
        </Error>
      );
    case 'INCORRECT_TRACKING_SCRIPT_INSTALLED':
      return (
        <Error
          title="Incorrect Tracking Script Installed"
          description="The tracking script installed on your website does NOT match the tracking script assigned your account. This is a simple issue to fix! Follow the instructions below to replace the mismatching script."
          suggestion={
            <span>
              <AppLink
                className="text-link"
                to={{ pathname: installPath, search: `?requestUrl=${encodeURIComponent(finalUrl)}` }}
              >
                Reinstall
              </AppLink>{' '}
              your Tracking Script. Then, re-check its installation.
            </span>
          }
          consoleErrors={consoleErrors}
        >
          <Button
            size="xl"
            className="mt-5"
            component={AppLink}
            to={{ pathname: installPath, search: `?requestUrl=${encodeURIComponent(finalUrl)}` }}
          >
            {installPath === DEFAULT_INSTALL_PATH ? 'Open Installation Guide' : 'View Install Instructions'}
          </Button>
        </Error>
      );
    case 'INACTIVE_ACCOUNT':
      return (
        <Error
          title="Inactive Account"
          description={
            <span>
              Your subscription is no longer active, please{' '}
              <AppLink className="text-link" to={manageSubscriptionPath}>
                choose a plan
              </AppLink>{' '}
              to reactivate your account.
            </span>
          }
        >
          <Button size="xl" className="mt-5" component={AppLink} to={manageSubscriptionPath}>
            Choose a Plan
          </Button>
        </Error>
      );
    case 'MISSING_TRACKING_SCRIPT':
      return (
        <Error
          title="Missing Tracking Script"
          description={
            <span>
              The tracking script was not detected on <strong className="break-all">{finalUrl}</strong>. If your site
              requires cookie acceptance, it could block our verification process.
            </span>
          }
          suggestion={
            <span>
              <SupportLink className="text-link" href={SupportLinks.scriptCheck.manual}>
                Verify manually
              </SupportLink>{' '}
              that the script is installed. Or{' '}
              <AppLink
                className="text-link"
                to={{ pathname: installPath, search: `?requestUrl=${encodeURIComponent(finalUrl)}` }}
              >
                install
              </AppLink>{' '}
              the tracking script.
            </span>
          }
          consoleErrors={consoleErrors}
        >
          <Button size="xl" className="mt-5" component={SupportLink} href={SupportLinks.scriptCheck.manual}>
            Check for Script Manually
          </Button>
        </Error>
      );
    case 'SELF_HOSTED_TRACKING_SCRIPT':
      return (
        <Error
          title="Self-Hosted Tracking Script"
          description={
            <span>
              It looks like you are self-hosting the Tracking Script. Only our Enterprise plans support self-hosting,
              please{' '}
              <SupportLink className="text-link" href={SupportLinks.general.newSupportRequest}>
                contact support
              </SupportLink>{' '}
              to learn more.
            </span>
          }
          consoleErrors={consoleErrors}
        >
          <Button
            size="xl"
            className="mt-5"
            component={AppLink}
            to={{ pathname: installPath, search: `?requestUrl=${encodeURIComponent(finalUrl)}` }}
          >
            {installPath === DEFAULT_INSTALL_PATH ? 'Open Installation Guide' : 'View Install Instructions'}
          </Button>
        </Error>
      );
    case 'CONTENT_SECURITY_POLICY':
      return (
        <Error
          title="Content Security Policy"
          description="This site implements a Content Security Policy that is blocking the tracking script from loading."
          suggestion={
            <span>
              Visit this{' '}
              <SupportLink className="text-link" href={SupportLinks.scriptCheck.contentSecurityPolicy}>
                help article
              </SupportLink>{' '}
              to learn more.
            </span>
          }
          consoleErrors={consoleErrors}
        >
          <Button
            size="xl"
            component={SupportLink}
            className="mt-5"
            href={SupportLinks.scriptCheck.contentSecurityPolicy}
          >
            View Help Article
          </Button>
        </Error>
      );
  }
}
